import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, BadgeCheck, Link2, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

/* Fee table data */
const feeRows = [
  { invoice: 100, fee: 3.29, receive: 96.71 },
  { invoice: 250, fee: 7.78, receive: 242.22 },
  { invoice: 500, fee: 15.25, receive: 484.75 },
  { invoice: 1000, fee: 30.20, receive: 969.80 },
];

const fmt = (n) => '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtWhole = (n) => '$' + n.toLocaleString('en-US');

/* FAQ data */
const faqItems = [
  {
    q: 'Is Servitium really free?',
    a: "Yes \u2014 for businesses operating in the Lower Mainland. There\u2019s no platform fee, no commission, and no success fee. The only fee on a transaction is Stripe\u2019s 2.99% + 30\u00a2, which Stripe charges directly. Servitium takes none of it.",
  },
  {
    q: 'What if I operate outside the Lower Mainland?',
    a: 'Servitium is $25/month. Your service area is verified at signup. You can cancel anytime.',
  },
  {
    q: 'Why is there a Stripe fee?',
    a: "Stripe is the payment processor that securely moves money from customers to your bank account. Every platform that accepts card payments uses a processor and charges this fee. We\u2019ve kept it at Stripe\u2019s standard rate \u2014 no markup.",
  },
  {
    q: 'How does identity verification work?',
    a: 'At signup, you complete a standard identity verification (the same kind banks and payment platforms use). It typically takes a few minutes.',
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. Lower Mainland businesses are free forever, so there\u2019s nothing to cancel. Outside the Lower Mainland, you can cancel your $25/month subscription anytime.",
  },
  {
    q: 'Is Servitium available outside Canada?',
    a: "Not yet. We\u2019re starting in BC.",
  },
];

/* Lower Mainland cities */
const lowerMainlandCities = [
  'Vancouver', 'Burnaby', 'Richmond', 'Surrey', 'Coquitlam', 'New Westminster',
  'Delta', 'North Vancouver', 'West Vancouver', 'Port Coquitlam', 'Port Moody',
  'Maple Ridge', 'Pitt Meadows', 'White Rock', 'Langley City', 'Langley Township',
  'Anmore', 'Belcarra', 'Bowen Island', 'Lions Bay',
];

/* Step data */
const steps = [
  { icon: BadgeCheck, label: 'STEP 1', title: 'VERIFY', body: "Set up your business and verify your identity. You\u2019re live in minutes." },
  { icon: Link2, label: 'STEP 2', title: 'CONNECT', body: 'Link your bank account through Stripe. Customers can now pay you in-app.' },
  { icon: ClipboardCheck, label: 'STEP 3', title: 'GET PAID', body: 'Customers pay through Servitium. Funds deposit to your bank.' },
];

const PricingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <main className="pt-16 md:pt-20" style={{ fontFamily: "'Open Sans', sans-serif" }} data-testid="pricing-page">
      <Helmet>
        <title>Pricing — Free across the Lower Mainland — Servitium</title>
        <meta name="description" content="Free for solo and micro-businesses in the Lower Mainland. No platform fee, no commission, no success fee. $25/month outside the Lower Mainland." />
        <meta property="og:title" content="Pricing — Free across the Lower Mainland — Servitium" />
        <meta property="og:description" content="Free for solo and micro-businesses in the Lower Mainland. No platform fee, no commission, no success fee. $25/month outside the Lower Mainland." />
        <meta property="og:url" content="https://servitium.ca/pricing" />
        <meta name="twitter:title" content="Pricing — Free across the Lower Mainland — Servitium" />
        <meta name="twitter:description" content="Free for solo and micro-businesses in the Lower Mainland. No platform fee, no commission, no success fee. $25/month outside the Lower Mainland." />
        <meta property="og:image" content="https://servitium.ca/og-preview.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Servitium — Infrastructure for solo and micro-businesses" />
        <meta name="twitter:image" content="https://servitium.ca/og-preview.png" />
      </Helmet>

      {/* ══ SECTION 1: PRICING HERO ══ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]" data-testid="pricing-hero">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#880808] mb-4">
            PRICING
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-6" data-testid="pricing-hero-headline">
            FREE ACROSS THE LOWER MAINLAND.
          </h1>
          <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto mb-8">
            Verified profile, structured job workflow, in-app payments, and a track record that follows you. No platform fee. No commission. No success fee. Forever.
          </p>
          <div className="flex flex-col items-center gap-4">
            <Link to="/get" data-testid="pricing-hero-cta">
              <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                Join free
              </Button>
            </Link>
            <div className="flex items-center gap-3" data-testid="pricing-hero-badges">
              <a href="#" aria-label="Download on the App Store">
                <img src="/apple-badge.svg" alt="App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
              <a href="#" aria-label="Get it on Google Play">
                <img src="/google-badge.svg" alt="Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION 2: THE ONLY FEE, EXPLAINED ══ */}
      <section className="py-20 md:py-28 bg-white" data-testid="pricing-fee-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight text-center mb-6" data-testid="pricing-fee-headline">
            THE ONLY FEE ON A SERVITIUM INVOICE IS STRIPE'S.
          </h2>
          <p className="text-base md:text-lg text-[#57534E] leading-relaxed text-center mb-3 max-w-2xl mx-auto">
            When a customer pays you through Servitium, the payment processor (Stripe) charges 2.99% + 30¢ per completed transaction. This is Stripe's standard rate — the same fee they charge every platform that uses them, including Shopify, Square, and Calendly.
          </p>
          <p className="text-base md:text-lg font-bold text-[#880808] text-center mb-10">
            Servitium takes nothing on top.
          </p>

          {/* Fee transparency table */}
          <div className="overflow-x-auto mb-14" data-testid="pricing-fee-table">
            <table className="w-full max-w-lg mx-auto text-sm">
              <thead>
                <tr className="border-b-2 border-[#E7E5E4]">
                  <th className="text-left py-3 px-4 text-xs font-bold tracking-[0.1em] uppercase text-[#57534E]">Invoice</th>
                  <th className="text-left py-3 px-4 text-xs font-bold tracking-[0.1em] uppercase text-[#57534E]">Stripe fee</th>
                  <th className="text-left py-3 px-4 text-xs font-bold tracking-[0.1em] uppercase text-[#57534E]">You receive</th>
                </tr>
              </thead>
              <tbody>
                {feeRows.map((row) => (
                  <tr key={row.invoice} className="border-b border-[#F5F5F4]">
                    <td className="py-3 px-4 text-[#36454F] font-semibold">{fmtWhole(row.invoice)}</td>
                    <td className="py-3 px-4 text-[#6b7280]">{fmt(row.fee)}</td>
                    <td className="py-3 px-4 text-[#36454F] font-bold">{fmt(row.receive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Outer-city subsection */}
          <div className="bg-[#F8F6F0] rounded-xl p-6 md:p-8" data-testid="pricing-outer-city">
            <h3 className="text-lg md:text-xl font-bold text-[#36454F] mb-2">
              OPERATING OUTSIDE THE LOWER MAINLAND?
            </h3>
            <p className="text-sm md:text-base text-[#57534E] leading-relaxed mb-4">
              Servitium is $25/month for businesses based outside the Lower Mainland. Your service area is verified at signup. Cancel anytime.
            </p>
            <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#880808] mb-2">
              Lower Mainland cities (free):
            </p>
            <p className="text-sm text-[#57534E] leading-relaxed">
              {lowerMainlandCities.join(' · ')}
            </p>
          </div>
        </div>
      </section>

      {/* ══ SECTION 3: HOW PAYMENTS WORK ══ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]" data-testid="pricing-payments-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight text-center mb-12" data-testid="pricing-payments-headline">
            HOW PAYMENTS WORK.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out flex flex-col text-center"
                data-testid={`pricing-step-${index}`}
              >
                <div className="h-[4px] bg-[#D4AF37] w-full" />
                <div className="p-6 md:p-8 flex flex-col items-center flex-grow">
                  <div className="w-14 h-14 rounded-full bg-[#880808]/10 flex items-center justify-center mb-5">
                    <step.icon className="w-7 h-7 text-[#880808]" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-bold tracking-[0.18em] text-[#D4AF37] uppercase mb-2">{step.label}</span>
                  <h3 className="text-xl md:text-2xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-3">{step.title}</h3>
                  <p className="text-[#57534E] text-sm md:text-base leading-relaxed flex-grow">{step.body}</p>
                </div>
                <div className="h-[3px] bg-[#E7E5E4] w-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SECTION 4: FAQ ══ */}
      <section className="py-20 md:py-28 bg-white" data-testid="pricing-faq-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight text-center mb-12">
            FREQUENTLY ASKED QUESTIONS
          </h2>

          <div className="space-y-0" data-testid="pricing-faq-list">
            {faqItems.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b border-[#E7E5E4]" data-testid={`pricing-faq-${i}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between py-5 text-left group"
                    data-testid={`pricing-faq-toggle-${i}`}
                  >
                    <span className="text-base md:text-lg font-semibold text-[#36454F] group-hover:text-[#880808] transition-colors pr-4">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#880808] flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      strokeWidth={2}
                    />
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300 ease-out"
                    style={{ maxHeight: isOpen ? 300 : 0, opacity: isOpen ? 1 : 0 }}
                  >
                    <p className="text-sm md:text-base text-[#57534E] leading-relaxed pb-5">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ SECTION 5: FINAL CTA ══ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]" data-testid="pricing-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-8" data-testid="pricing-final-headline">
            RUN YOUR BUSINESS ON A SYSTEM,<br />NOT A STRING OF TEXT MESSAGES.
          </h2>
          <div className="flex flex-col items-center gap-4 mb-4">
            <Link to="/get" data-testid="pricing-final-cta-btn">
              <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                Join free
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <a href="#" aria-label="Download on the App Store">
                <img src="/apple-badge.svg" alt="App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
              <a href="#" aria-label="Get it on Google Play">
                <img src="/google-badge.svg" alt="Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
            </div>
          </div>
          <p className="text-sm text-[#6b7280]">
            Free across the Lower Mainland · $25/month elsewhere
          </p>
        </div>
      </section>

    </main>
  );
};

export default PricingPage;
